import React, { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  FileText,
  Edit2,
  Trash2,
  Plus,
  Clock,
  Calendar,
  Check,
  AlertCircle,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Download,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { postsAPI } from '../../services/api';

// Types
type Mode = 'csv' | 'compose' | 'calendar';
type PlatformName =
  | 'Instagram'
  | 'Facebook'
  | 'Twitter'
  | 'LinkedIn'
  | 'TikTok'
  | 'YouTube'
  | 'Pinterest'
  | 'Telegram';

interface TimeSlot {
  date: string;
  time: string;
}

interface PostItem {
  id: string;
  content: string;
  platforms: PlatformName[];
  scheduledDate: string;
  scheduledTime: string;
  mediaUrl?: string;
  status: 'draft' | 'scheduled' | 'created';
}

// Platform config
const PLATFORMS: PlatformName[] = [
  'Instagram',
  'Facebook',
  'Twitter',
  'LinkedIn',
  'TikTok',
  'YouTube',
  'Pinterest',
  'Telegram',
];

const PLATFORM_COLORS: Record<PlatformName, string> = {
  Instagram: '#E4405F',
  Facebook: '#1877F2',
  Twitter: '#1DA1F2',
  LinkedIn: '#0A66C2',
  TikTok: '#000000',
  YouTube: '#FF0000',
  Pinterest: '#E60023',
  Telegram: '#0088cc',
};

const getPlatformInitial = (platform: PlatformName): string =>
  platform.charAt(0).toUpperCase();

// ─── Robust CSV Parser (handles quoted fields, commas in content) ───
function parseCSV(csv: string): Record<string, string>[] {
  const rows: Record<string, string>[] = [];
  const lines = csv.trim().split('\n');
  if (lines.length < 2) return [];

  const parseRow = (line: string): string[] => {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i++; // skip escaped quote
          } else {
            inQuotes = false;
          }
        } else {
          current += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ',') {
          fields.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
    }
    fields.push(current.trim());
    return fields;
  };

  const headers = parseRow(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, '_'));
  const requiredCols = ['content', 'platform', 'scheduled_date'];
  const missing = requiredCols.filter((c) => !headers.includes(c));
  if (missing.length > 0) {
    throw new Error(`CSV missing required columns: ${missing.join(', ')}`);
  }

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const fields = parseRow(line);
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = fields[idx] || '';
    });
    rows.push(row);
  }

  return rows;
}

// ─── CSV Sample Download ───
function downloadSampleCSV() {
  const sample = `content,platform,scheduled_date,media_url
"Exciting news coming soon! Stay tuned 🚀",Instagram,2026-03-25T10:00,
"Check out our latest blog post",Twitter,2026-03-25T14:00,https://example.com/image.jpg
"We're hiring! Join our team",LinkedIn,2026-03-26T09:00,`;
  const blob = new Blob([sample], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bulk-schedule-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ─── CSV Upload Mode ───
const CSVUploadMode: React.FC<{
  onPosts: (posts: PostItem[]) => void;
  t: (key: string, fallback: string) => string;
}> = ({ onPosts, t }) => {
  const [dragActive, setDragActive] = useState(false);
  const [parsedRows, setParsedRows] = useState<PostItem[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rowEditId = React.useId();

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files?.[0]) processFile(files[0]);
  };

  const processFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error(t('bulk_msg_csv_only', 'Please upload a CSV file'));
      return;
    }
    setParseError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target?.result as string;
        const rows = parseCSV(csv);

        const posts: PostItem[] = rows.map((row, idx) => {
          const dateStr = row.scheduled_date || '';
          const [date, time] = dateStr.includes('T')
            ? dateStr.split('T')
            : [dateStr, '10:00'];

          return {
            id: `csv-${Date.now()}-${idx}`,
            content: row.content || '',
            platforms: row.platform
              ? (row.platform.split(';').map((p) => p.trim()) as PlatformName[])
              : [],
            scheduledDate: date,
            scheduledTime: time,
            mediaUrl: row.media_url || undefined,
            status: 'draft',
          };
        });

        setParsedRows(posts);
        onPosts(posts);
        toast.success(t('bulk_msg_parsed_csv', `Parsed ${posts.length} posts from CSV`));
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Failed to parse CSV';
        setParseError(msg);
        toast.error(msg);
      }
    };
    reader.readAsText(file);
  };

  const deleteRow = (id: string) => {
    const updated = parsedRows.filter((r) => r.id !== id);
    setParsedRows(updated);
    onPosts(updated);
  };

  const handleEditStart = (row: PostItem) => {
    setEditingId(row.id);
    setEditingContent(row.content);
  };

  const handleEditSave = (id: string) => {
    const updated = parsedRows.map((r) =>
      r.id === id ? { ...r, content: editingContent } : r
    );
    setParsedRows(updated);
    onPosts(updated);
    setEditingId(null);
    setEditingContent('');
  };

  return (
    <div className="space-y-4">
      {/* Sample CSV download */}
      <div className="flex justify-end">
        <button
          onClick={downloadSampleCSV}
          className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-700 transition-all"
        >
          <Download className="w-4 h-4" />
          {t('bulk_btn_download_sample_csv', 'Download sample CSV')}
        </button>
      </div>

      {/* Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className="border-2 border-dashed rounded-n p-6 text-center transition-all duration-200"
        style={{
          borderColor: dragActive ? '#2383e2' : '#ebebea',
          backgroundColor: dragActive ? '#e8f0fe' : '#f7f7f5',
        }}
      >
        <Upload className="w-8 h-8 mx-auto mb-2 text-n-text-lt" />
        <p className="text-n-text font-bold text-sm">{t('bulk_msg_drag_drop_csv', 'Drag and drop your CSV file here')}</p>
        <p className="text-n-text-md text-xs mt-1">
          {t('bulk_msg_csv_required', 'Required')}: <span className="font-semibold">content</span>, <span className="font-semibold">platform</span>, <span className="font-semibold">scheduled_date</span> — {t('bulk_msg_csv_optional', 'Optional')}: <span className="font-semibold">media_url</span>
        </p>
        <p className="text-n-text-lt text-[10px] mt-1">
          {t('bulk_msg_semicolons_platforms', 'Use semicolons to target multiple platforms (e.g. "Instagram;Facebook")')}
        </p>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="mt-3 px-4 py-2 rounded-n-sm text-white text-xs font-bold transition-all hover:opacity-90"
          style={{ backgroundColor: '#2383e2' }}
        >
          {t('bulk_btn_browse_files', 'Browse Files')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.[0]) processFile(e.target.files[0]);
          }}
        />
      </div>

      {/* Parse error */}
      {parseError && (
        <div className="flex items-center gap-2 p-3 rounded-n-sm bg-n-red-bg border border-n-red text-n-red text-xs font-medium">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {parseError}
        </div>
      )}

      {/* Preview Table */}
      {parsedRows.length > 0 ? (
        <div className="rounded-n overflow-hidden border border-n-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-n-border bg-n-page">
                <th className="text-left px-3 py-2 font-normal text-n-text-lt text-xs w-8">#</th>
                <th className="text-left px-3 py-2 font-normal text-n-text-lt text-xs">{t('bulk_col_content', 'Content')}</th>
                <th className="text-left px-3 py-2 font-normal text-n-text-lt text-xs">{t('bulk_col_platforms', 'Platform(s)')}</th>
                <th className="text-left px-3 py-2 font-normal text-n-text-lt text-xs">{t('bulk_col_datetime', 'Date & Time')}</th>
                <th className="text-center px-3 py-2 font-normal text-n-text-lt text-xs">{t('bulk_col_actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {parsedRows.map((row, idx) => (
                <tr key={row.id} className="border-t border-n-border hover:bg-n-hover transition-colors">
                  <td className="px-3 py-2 text-n-text-lt text-xs font-semibold">{idx + 1}</td>
                  <td className="px-3 py-2 text-n-text max-w-xs">
                    {editingId === row.id ? (
                      <div className="flex gap-2">
                        <label htmlFor={`${rowEditId}-${row.id}`} className="sr-only">{t('bulk_label_edit_content', 'Edit content')}</label>
                        <textarea
                          id={`${rowEditId}-${row.id}`}
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-n-border rounded-n-xs text-n-text text-xs focus:outline-none focus:ring-1 focus:ring-n-blue"
                          rows={2}
                        />
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleEditSave(row.id)}
                            className="p-1.5 hover:bg-n-green-bg rounded text-n-green transition-colors"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => { setEditingId(null); setEditingContent(''); }}
                            className="p-1.5 hover:bg-n-red-bg rounded text-n-red transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="truncate text-sm">{row.content}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {row.platforms.map((p) => (
                        <span
                          key={p}
                          className="inline-block px-2 py-0.5 rounded-full text-white text-[10px] font-bold"
                          style={{ backgroundColor: PLATFORM_COLORS[p] || '#787774' }}
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-n-text whitespace-nowrap text-xs">
                    {row.scheduledDate} {row.scheduledTime}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {editingId !== row.id && (
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleEditStart(row)}
                          className="p-1.5 hover:bg-n-hover rounded text-n-text-md transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => deleteRow(row.id)}
                          className="p-1.5 hover:bg-n-red-bg rounded text-n-red transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-n border border-n-border p-6 text-center bg-n-card">
          <FileText className="w-8 h-8 mx-auto text-n-text-lt mb-2" />
          <p className="text-n-text font-bold text-sm">{t('bulk_msg_no_csv_rows', 'No CSV rows parsed yet')}</p>
          <p className="text-n-text-md text-xs mt-1">{t('bulk_msg_upload_csv_start', 'Upload a CSV file to get started')}</p>
        </div>
      )}
    </div>
  );
};

// ─── Bulk Compose Mode ───
const BulkComposeMode: React.FC<{
  onPosts: (posts: PostItem[]) => void;
  t: (key: string, fallback: string) => string;
}> = ({ onPosts, t }) => {
  const [caption, setCaption] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<PlatformName[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([{ date: '', time: '10:00' }]);
  const [mediaUrl, setMediaUrl] = useState('');

  // Accessibility IDs
  const captionId = React.useId();
  const mediaUrlId = React.useId();
  const dateIdPrefix = React.useId();
  const timeIdPrefix = React.useId();

  const togglePlatform = (platform: PlatformName) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const addTimeSlot = () => setTimeSlots([...timeSlots, { date: '', time: '10:00' }]);

  const removeTimeSlot = (idx: number) => setTimeSlots(timeSlots.filter((_, i) => i !== idx));

  const updateSlot = (idx: number, field: 'date' | 'time', value: string) => {
    const newSlots = [...timeSlots];
    newSlots[idx] = { ...newSlots[idx], [field]: value };
    setTimeSlots(newSlots);
  };

  const handleGenerate = () => {
    if (!caption.trim()) {
      toast.error(t('bulk_msg_enter_caption', 'Please enter a caption'));
      return;
    }
    if (selectedPlatforms.length === 0) {
      toast.error(t('bulk_msg_select_platform', 'Please select at least one platform'));
      return;
    }
    if (timeSlots.some((slot) => !slot.date)) {
      toast.error(t('bulk_msg_fill_dates', 'Please fill all date fields'));
      return;
    }

    const posts: PostItem[] = timeSlots.map((slot, idx) => ({
      id: `compose-${Date.now()}-${idx}`,
      content: caption,
      platforms: selectedPlatforms,
      scheduledDate: slot.date,
      scheduledTime: slot.time,
      mediaUrl: mediaUrl || undefined,
      status: 'draft',
    }));

    onPosts(posts);
    toast.success(t('bulk_msg_generated_posts', `Generated ${posts.length} posts — review and click "Schedule All" to submit`));
  };

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      {/* Caption */}
      <div>
        <label htmlFor={captionId} className="block text-xs font-medium text-n-text-md mb-1.5">{t('bulk_label_caption', 'Caption')}</label>
        <textarea
          id={captionId}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder={t('bulk_placeholder_caption', 'Type your caption here...')}
          className="w-full px-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:ring-1 focus:ring-n-blue bg-n-page text-n-text resize-none text-sm"
          rows={3}
        />
        <p className="text-[10px] text-n-text-lt mt-1">{caption.length} {t('bulk_label_characters', 'characters')}</p>
      </div>

      {/* Media URL */}
      <div>
        <label htmlFor={mediaUrlId} className="block text-xs font-medium text-n-text-md mb-1.5">{t('bulk_label_media_url', 'Media URL')} <span className="font-normal text-n-text-lt">({t('bulk_label_optional', 'optional')})</span></label>
        <input
          id={mediaUrlId}
          type="url"
          value={mediaUrl}
          onChange={(e) => setMediaUrl(e.target.value)}
          placeholder="https://example.com/image.jpg"
          className="w-full px-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:ring-1 focus:ring-n-blue bg-n-page text-n-text text-sm"
        />
      </div>

      {/* Platforms */}
      <div>
        <p className="block text-xs font-medium text-n-text-md mb-2">{t('bulk_label_platforms', 'Platforms')}</p>
        <div className="flex flex-wrap gap-2">
          {PLATFORMS.map((platform) => (
            <button
              key={platform}
              onClick={() => togglePlatform(platform)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all text-xs font-bold ${
                selectedPlatforms.includes(platform)
                  ? 'text-white'
                  : 'bg-n-hover text-n-text hover:bg-n-border'
              }`}
              style={{
                backgroundColor: selectedPlatforms.includes(platform)
                  ? PLATFORM_COLORS[platform]
                  : undefined,
              }}
            >
              <div
                className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold"
                style={{
                  backgroundColor: selectedPlatforms.includes(platform)
                    ? 'rgba(255,255,255,0.3)'
                    : '#ccc',
                }}
              >
                {getPlatformInitial(platform)}
              </div>
              {platform}
            </button>
          ))}
        </div>
      </div>

      {/* Time Slots */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="block text-xs font-medium text-n-text-md">{t('bulk_label_schedule_times', 'Schedule Times')}</span>
          <button
            onClick={addTimeSlot}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-n-sm text-white text-xs font-bold transition-all"
            style={{ backgroundColor: '#2383e2' }}
          >
            <Plus className="w-3.5 h-3.5" />
            {t('bulk_btn_add_slot', 'Add Slot')}
          </button>
        </div>

        <div className="space-y-2">
          {timeSlots.map((slot, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <span className="text-[10px] text-n-text-lt font-bold w-5 text-center">{idx + 1}.</span>
              <label htmlFor={`${dateIdPrefix}-${idx}`} className="sr-only">{t('bulk_label_date_slot', `Date for slot ${idx + 1}`)}</label>
              <input
                id={`${dateIdPrefix}-${idx}`}
                type="date"
                value={slot.date}
                onChange={(e) => updateSlot(idx, 'date', e.target.value)}
                className="flex-1 px-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:ring-1 focus:ring-n-blue bg-n-page text-n-text text-sm"
              />
              <label htmlFor={`${timeIdPrefix}-${idx}`} className="sr-only">{t('bulk_label_time_slot', `Time for slot ${idx + 1}`)}</label>
              <input
                id={`${timeIdPrefix}-${idx}`}
                type="time"
                value={slot.time}
                onChange={(e) => updateSlot(idx, 'time', e.target.value)}
                className="w-28 px-3 py-2 border border-n-border rounded-n-sm focus:outline-none focus:ring-1 focus:ring-n-blue bg-n-page text-n-text text-sm"
              />
              {timeSlots.length > 1 && (
                <button
                  onClick={() => removeTimeSlot(idx)}
                  className="p-1.5 hover:bg-n-red-bg rounded transition-colors text-n-red"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        className="w-full px-4 py-2.5 rounded-n-sm text-white font-bold text-sm transition-all hover:opacity-90"
        style={{ backgroundColor: '#2383e2' }}
      >
        {t('bulk_btn_generate_posts_review', 'Generate Posts for Review')}
      </button>
    </div>
  );
};

// ─── Calendar View Mode (wired to real API) ───
const CalendarViewMode: React.FC<{
  localPosts: PostItem[];
  t: (key: string, fallback: string) => string;
}> = ({ localPosts, t }) => {
  const [weekOffset, setWeekOffset] = useState(0);

  const getWeekDates = (offset: number) => {
    const today = new Date();
    const day = today.getDay();
    const start = new Date(today);
    start.setDate(today.getDate() - day + offset * 7);
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const weekDates = getWeekDates(weekOffset);
  const startStr = weekDates[0].toISOString().split('T')[0];
  const endStr = weekDates[6].toISOString().split('T')[0];

  // Fetch real scheduled posts from API for this week
  const { data: calendarData } = useQuery({
    queryKey: ['calendar', startStr, endStr],
    queryFn: async () => {
      const { data } = await postsAPI.calendar(startStr, endStr);
      return data.events as Array<{
        id: string;
        title: string;
        start: string;
        status: string;
        platforms: string[];
        media_count: number;
      }>;
    },
    staleTime: 60_000,
  });

  const todayStr = new Date().toISOString().split('T')[0];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getPostsForDate = (dateStr: string) => {
    // Merge API events + local unsaved posts
    const apiPosts = (calendarData || [])
      .filter((e) => e.start?.startsWith(dateStr))
      .map((e) => ({
        id: e.id,
        content: e.title,
        platforms: (e.platforms || []) as PlatformName[],
        time: e.start?.split('T')[1]?.slice(0, 5) || '',
        status: e.status,
        source: 'api' as const,
      }));

    const localForDate = localPosts
      .filter((p) => p.scheduledDate === dateStr)
      .map((p) => ({
        id: p.id,
        content: p.content,
        platforms: p.platforms,
        time: p.scheduledTime,
        status: p.status,
        source: 'local' as const,
      }));

    return [...apiPosts, ...localForDate];
  };

  return (
    <div className="space-y-3">
      {/* Navigation */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setWeekOffset(weekOffset - 1)}
          className="p-1.5 rounded-n-sm border border-n-border hover:bg-n-hover transition-all text-n-text-md"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h3 className="text-sm font-bold text-n-text min-w-[200px] text-center">
          {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} –{' '}
          {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </h3>
        <button
          onClick={() => setWeekOffset(weekOffset + 1)}
          className="p-1.5 rounded-n-sm border border-n-border hover:bg-n-hover transition-all text-n-text-md"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        {weekOffset !== 0 && (
          <button
            onClick={() => setWeekOffset(0)}
            className="text-xs font-semibold hover:underline transition-colors ml-1 text-n-blue"
          >
            {t('bulk_btn_today', 'Today')}
          </button>
        )}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-px bg-n-border rounded-n overflow-hidden">
        {weekDates.map((date, idx) => {
          const dateStr = date.toISOString().split('T')[0];
          const dayPosts = getPostsForDate(dateStr);
          const isToday = dateStr === todayStr;

          return (
            <div
              key={idx}
              className="p-2 bg-n-card min-h-[120px]"
              style={isToday ? { boxShadow: 'inset 0 2px 0 0 #2383e2' } : undefined}
            >
              <div className="flex items-center gap-1 mb-1.5">
                <p className="font-bold text-n-text text-[10px] uppercase">{dayNames[date.getDay()]}</p>
                <p
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                    isToday ? 'text-white' : 'text-n-text-md bg-n-page'
                  }`}
                  style={{ backgroundColor: isToday ? '#2383e2' : undefined }}
                >
                  {date.getDate()}
                </p>
              </div>

              <div className="space-y-1.5">
                {dayPosts.map((post) => (
                  <div
                    key={post.id}
                    className={`p-1.5 rounded-n-xs text-[10px] space-y-1 border transition-colors ${
                      post.source === 'local'
                        ? 'bg-n-amber-bg border-n-amber'
                        : 'bg-n-page border-n-border'
                    }`}
                  >
                    {post.time && (
                      <p className="text-n-text font-bold flex items-center gap-1">
                        <Clock className="w-3 h-3 text-n-green" />
                        {post.time}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-0.5">
                      {post.platforms.map((platform) => (
                        <div
                          key={platform}
                          className="w-4 h-4 rounded-full flex items-center justify-center text-white font-bold"
                          style={{
                            backgroundColor: PLATFORM_COLORS[platform as PlatformName] || '#787774',
                            fontSize: '7px',
                          }}
                          title={platform}
                        >
                          {getPlatformInitial(platform as PlatformName)}
                        </div>
                      ))}
                    </div>
                    <p className="text-n-text line-clamp-2 font-medium">{post.content}</p>
                    {post.source === 'local' && (
                      <span className="inline-block px-1.5 py-0.5 rounded-n-xs bg-n-amber-bg text-n-amber text-[9px] font-bold">
                        {t('bulk_label_unsaved', 'Unsaved')}
                      </span>
                    )}
                  </div>
                ))}
                {dayPosts.length === 0 && (
                  <p className="text-n-text-lt text-[10px] text-center mt-3">{t('bulk_msg_no_posts_day', 'No posts')}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main Component ───
export default function BulkSchedulePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<Mode>('compose');
  const [allPosts, setAllPosts] = useState<PostItem[]>([]);

  // Bulk create mutation
  const bulkCreateMutation = useMutation({
    mutationFn: async (posts: PostItem[]) => {
      const payload = posts.map((p) => ({
        text: p.content,
        target_accounts: [], // user would pick accounts in a full flow
        scheduled_at: `${p.scheduledDate}T${p.scheduledTime}:00`,
        media_ids: [],
        hashtags: [],
        mentions: [],
        platform_overrides: {},
      }));
      const { data } = await postsAPI.bulkCreate(payload);
      return data;
    },
    onSuccess: (data) => {
      toast.success(t('bulk_msg_scheduled_success', `${data.created?.length || 0} posts scheduled successfully!`));
      if (data.errors?.length > 0) {
        toast.error(t('bulk_msg_schedule_failed', `${data.errors.length} posts failed — check your data`));
      }
      // Mark local posts as created
      setAllPosts((prev) => prev.map((p) => ({ ...p, status: 'created' as const })));
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
    onError: () => {
      toast.error(t('bulk_msg_failed_schedule', 'Failed to schedule posts. Please try again.'));
    },
  });

  const summary = useMemo(() => {
    const uniquePlatforms = new Set<PlatformName>();
    const dates = new Set<string>();
    allPosts.forEach((post) => {
      post.platforms.forEach((p) => uniquePlatforms.add(p));
      dates.add(post.scheduledDate);
    });
    const sortedDates = Array.from(dates).sort();
    return {
      totalPosts: allPosts.length,
      platformsCount: uniquePlatforms.size,
      dateRange:
        sortedDates.length > 0
          ? `${sortedDates[0]} → ${sortedDates[sortedDates.length - 1]}`
          : t('bulk_label_no_dates', 'No dates set'),
    };
  }, [allPosts, t]);

  const handleScheduleAll = () => {
    const validPosts = allPosts.filter((p) => p.status !== 'created');
    if (validPosts.length === 0) {
      toast.error(t('bulk_msg_no_posts_schedule', 'No posts to schedule'));
      return;
    }
    // Validate all have dates
    const invalid = validPosts.filter((p) => !p.scheduledDate || !p.content.trim());
    if (invalid.length > 0) {
      toast.error(t('bulk_msg_missing_content_dates', `${invalid.length} posts are missing content or dates`));
      return;
    }
    bulkCreateMutation.mutate(validPosts);
  };

  const pendingCount = allPosts.filter((p) => p.status !== 'created').length;

  return (
    <div className="h-full flex flex-col bg-n-card overflow-hidden">
      {/* Page Header */}
      <div className="border-b border-n-border px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="text-3xl">📦</span>
              <h1 className="text-4xl font-bold text-n-text">Bulk Schedule</h1>
            </div>
            <p className="text-sm text-n-text-md">{t('bulk_subtitle', 'Schedule posts in bulk using CSV, compose mode, or calendar view')}</p>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="space-y-4">
          {/* Toolbar: tabs + summary + actions */}
          <div className="flex items-center gap-2">
            {/* Mode Tabs */}
            <div className="flex items-center gap-0.5 border-b border-n-border">
              {useMemo(() => [
                { id: 'csv' as Mode, label: t('bulk_tab_csv_upload', 'CSV Upload'), icon: FileText },
                { id: 'compose' as Mode, label: t('bulk_tab_compose', 'Compose'), icon: Edit2 },
                { id: 'calendar' as Mode, label: t('bulk_tab_calendar', 'Calendar'), icon: Calendar },
              ] as const, [t]).map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setMode(id)}
                  className={`flex items-center gap-1.5 px-3 py-2 font-medium text-xs transition-colors relative ${
                    mode === id
                      ? 'text-n-blue'
                      : 'text-n-text-md hover:text-n-text'
                  }`}
                  style={mode === id ? { borderBottomColor: '#2383e2', borderBottomWidth: '2px', marginBottom: '-1px' } : undefined}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {/* Summary badges */}
            {allPosts.length > 0 && (
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="text-[10px] font-semibold px-2 py-1 rounded-n-xs bg-n-blue-bg text-n-blue">
                  {summary.totalPosts} {t('bulk_label_posts', 'posts')}
                </span>
                <span className="text-[10px] font-semibold px-2 py-1 rounded-n-xs bg-n-page text-n-text-md">
                  {summary.platformsCount} {t('bulk_label_platforms', 'platforms')}
                </span>
                {pendingCount < summary.totalPosts && (
                  <span className="text-[10px] font-semibold px-2 py-1 rounded-n-xs bg-n-green-bg text-n-green">
                    {summary.totalPosts - pendingCount} {t('bulk_label_scheduled', 'scheduled')}
                  </span>
                )}
              </div>
            )}

            {/* Actions */}
            {pendingCount > 0 && (
              <div className="flex items-center gap-1.5 ml-auto">
                <button
                  onClick={() => setAllPosts([])}
                  className="px-2.5 py-1.5 text-xs font-medium text-n-text-md hover:text-n-red hover:bg-n-red-bg rounded-n-sm transition-all"
                >
                  {t('bulk_btn_clear', 'Clear')}
                </button>
                <button
                  onClick={handleScheduleAll}
                  disabled={bulkCreateMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-n-sm text-white text-xs font-bold transition-all disabled:opacity-60"
                  style={{ backgroundColor: '#2383e2' }}
                >
                  {bulkCreateMutation.isPending ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      {t('bulk_btn_scheduling', 'Scheduling...')}
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      {t('bulk_btn_schedule_all', `Schedule All (${pendingCount})`)}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Content Card */}
          <div className="bg-n-card border border-n-border rounded-n-sm p-4 shadow-n-sm">
            {mode === 'csv' && <CSVUploadMode onPosts={setAllPosts} t={t} />}
            {mode === 'compose' && <BulkComposeMode onPosts={setAllPosts} t={t} />}
            {mode === 'calendar' && <CalendarViewMode localPosts={allPosts.filter((p) => p.status !== 'created')} t={t} />}
          </div>
        </div>
      </div>
    </div>
  );
}
